import React, {
  useState,
  useEffect,
  useCallback,
  useMemo,
  useRef,
} from 'react';
import styled from 'styled-components';

import { getChainPublicApi } from '#/api/apiCoreSdk';
import useTermsConfirmModal from '#/components/auth/Legal/TermsModal/TermsModal';
import ErrorPanel from '#/components/basic/panels/ErrorPanel';
import Tooltip from '#/components/basic/tooltip/Tooltip';
import { getWalletIcon } from '#/components/wallet/icons/walletIcon';
import QrSyncMobile from '#/components/wallet/mobileSync/mobile/QrSyncMobile';
import { HIDDEN_DISCOVERED_WALLET_RDNS } from '#/config/constants';
import { useIsMobile } from '#/hooks/useIsMobile';
import { useIsRestrictedRegion } from '#/hooks/useIsRestrictedRegion';
import {
  ETH_USER_REJECTED_REQUEST_ERROR_CODE,
  makeEthersErrorHumanRedable,
} from '#/lib/ethereum/ethersErrors';
import { getSupportedOneClickWallet } from '#/lib/ethereum/wallets';
import { useSelector } from '#/reduxState/store';
import { Wallet, WalletType } from '#/reduxState/wallet/walletUtils';

import { useWalletSignPromptModal } from '../WalletSignPromptOverlayModal';

import ConnectedWallet from './ConnectedWallet';
import {
  ProvidersMap,
  useDiscoverEip6963Wallets,
} from './discovery/walletDiscoveryEip6963';
import RememberMe from './RememberMe';
import UnlockSuccess from './shared/UnlockSuccess';
import WalletMenuItem from './UnlockItem';
import UnlockLayout from './UnlockLayout';
import { renderWalletsMap } from './walletsList';
import { walletsList } from './walletsListCore';
import { handleUnlock } from './walletsUtils';

const ListWrap = styled.ul`
  list-style: none;
  padding: 0;
  margin: 0;
  display: grid;
  grid-template-columns: 1fr 1fr;
  gap: 10px;
  padding-bottom: 24px;

  .isMobileStyle & {
    grid-template-columns: 1fr;
    min-width: 250px;
    max-width: 400px;
    margin: 20px auto;
  }
`;

type Params = {
  afterUnlockCallback?: () => void;
  afterUnlockGoTo?: string;
  delegationDisabled?: boolean;
  keepWalletUntilSuccess: boolean;
  layout: 'page' | 'modal';
};

function UnlockMainCore({
  afterUnlockCallback,
  afterUnlockGoTo,
  delegationDisabled,
  keepWalletUntilSuccess,
  layout,
  discoveredMap,
}: Params & { discoveredMap: ProvidersMap }) {
  const isMobile = useIsMobile();

  const preSelectWallet = useSelector(
    state => state.walletDetail.preSelectWallet
  );
  const preSelectRemember = useSelector(
    state => state.walletDetail.preSelectRemember
  );

  const oneClickWallet =
    preSelectWallet || getSupportedOneClickWallet(discoveredMap, isMobile);
  const isRestrictedRegion = useIsRestrictedRegion();

  const referralCode = useSelector(
    state => state.currentUserData.userReferral.referralCode
  );
  const bankrollCodeToApply = useSelector(
    state => state.currentUserData.userBankroll.bankrollCodeToApply
  );
  const bankrollCodeType = useSelector(
    state => state.currentUserData.userBankroll.bankrollCodeType
  );
  const welcomeBonusCode =
    bankrollCodeType === 'welcomePoints' ? bankrollCodeToApply : undefined;

  const currentWalletType = useSelector(state => state.wallet.walletType);
  const currentWalletRdns = useSelector(
    state => state.wallet.walletDiscoveryRdns
  );
  // const { activeChain } = useSelectorActiveChain();
  /** For unlock we only allow main chain */
  const activeChain = 'katanaEVMChain';

  const [unlockingWallet, setUnlockingWallet] = useState<
    { type: WalletType; rdns?: string } | undefined
  >(oneClickWallet ? preSelectWallet : undefined);

  const walletObjBeforeAcceptTerms = useRef<Wallet>();

  const [remember, setRemember] = useState(preSelectRemember ?? true);
  const [quickUnlockError, setQuickUnlockError] = useState('');
  const [quickUnlockState, setQuickUnlockState] = useState<
    'default' | 'acceptTerms' | 'unlocking' | 'signing' | 'success'
  >('default');

  const { walletSignPromptPlaceholder, walletSignTitle } =
    useWalletSignPromptModal();

  useEffect(() => {
    if (walletSignPromptPlaceholder && quickUnlockState !== 'signing') {
      // eslint-disable-next-line react-hooks/set-state-in-effect
      setQuickUnlockState('signing');
    }
  }, [walletSignPromptPlaceholder, quickUnlockState]);

  const getWalletAsync = useCallback(
    (selectWallet: WalletType, rdns?: string) => {
      if (selectWallet === 'qrSynced') {
        return;
      }
      setUnlockingWallet({ type: selectWallet, rdns });
      const selectedWalletObject = {
        ...renderWalletsMap[selectWallet],
        wallet: selectWallet,
      };
      return selectedWalletObject?.getWalletAsync?.({ activeChain, rdns });
    },
    [activeChain]
  );

  const handleAsyncUnlock = useCallback(
    async (
      selectWallet: WalletType,
      rdns?: string,
      tosAcceptedOverride = false
    ) => {
      if (selectWallet !== 'qrSynced' && !tosAcceptedOverride) {
        // We have to store wallet object, otherwise could cause problems like
        // duplicated modal while connecting WalletConnect
        walletObjBeforeAcceptTerms.current = await getWalletAsync(
          selectWallet,
          rdns
        );
        if (!walletObjBeforeAcceptTerms.current?.walletAddress) {
          throw new Error('Unknown wallet');
        }
        const { tosAccepted } = await getChainPublicApi(
          activeChain
        ).getInternalPublicWalletSettings(
          walletObjBeforeAcceptTerms.current.walletAddress
        );
        if (!tosAccepted) {
          console.log('ToS not accepted - display modal');
          setQuickUnlockState('acceptTerms');
          return;
        } else {
          console.log('ToS Already Accepted - continue');
        }
      }

      const walletObj =
        walletObjBeforeAcceptTerms.current ||
        (await getWalletAsync(selectWallet, rdns));

      walletObjBeforeAcceptTerms.current = undefined;

      if (!walletObj) {
        // Expected for Mobile Connect
        return;
      }

      const delegatedKeyType: false | 'session' | 'persistent' =
        delegationDisabled ? false : remember ? 'persistent' : 'session';

      // This state is only active until wallet is ready - eg. before first MetaMask unlock
      // and so it can be skipped completelly for subsequent unlocks
      setTimeout(
        () =>
          setQuickUnlockState(state =>
            state === 'default' || state === 'acceptTerms' ? 'unlocking' : state
          ),
        500
      );
      setQuickUnlockError('');

      // Hotfix: Unknown MM failures
      // if (selectWallet === 'walletConnect') {
      //   await waitAsync(200);
      // }

      return handleUnlock(
        walletObj,
        delegatedKeyType,
        {
          afterUnlockCallback,
          redirectTo: afterUnlockGoTo,
          promotions: {
            referralCode,
            welcomeBonusCode,
          },
        },
        keepWalletUntilSuccess
      )
        .then(() => {
          setQuickUnlockState('success');
        })
        .catch(error => {
          console.warn(error);
          setQuickUnlockState('default');
          setUnlockingWallet(undefined);
          if (error?.code === ETH_USER_REJECTED_REQUEST_ERROR_CODE) {
            return;
          }
          setQuickUnlockError(makeEthersErrorHumanRedable(error));
        });
    },
    // TODO: katana referralCode causing problems with second sign trigger
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [
      getWalletAsync,
      delegationDisabled,
      remember,
      // referralCode,
      afterUnlockCallback,
      afterUnlockGoTo,
      keepWalletUntilSuccess,
    ]
  );

  const { handleShowTermsConfirmation, termsConfirmationPlaceholder } =
    useTermsConfirmModal(
      () => {
        if (!unlockingWallet) {
          setQuickUnlockError('Wallet not available');
          return;
        }
        setQuickUnlockState('unlocking');
        handleAsyncUnlock(unlockingWallet.type, unlockingWallet.rdns, true);
      },
      () => {
        setQuickUnlockError('Terms not accepted');
        setQuickUnlockState('default');
        setUnlockingWallet(undefined);
        walletObjBeforeAcceptTerms.current = undefined;
      }
    );

  useEffect(() => {
    if (quickUnlockState === 'acceptTerms') {
      handleShowTermsConfirmation();
    }
  }, [handleShowTermsConfirmation, quickUnlockState]);

  // Unlock flow initiated by Connect click,
  // or automatic by pre-selected wallet, or one-click wallet
  // Warning: Be careful about dependency loop !
  useEffect(() => {
    if (unlockingWallet?.type) {
      // eslint-disable-next-line react-hooks/set-state-in-effect
      handleAsyncUnlock(unlockingWallet.type, unlockingWallet.rdns).catch(
        error => {
          console.warn(error);
          setQuickUnlockError(makeEthersErrorHumanRedable(error));
          setQuickUnlockState('default');
          setUnlockingWallet(undefined);
        }
      );
    }
  }, [handleAsyncUnlock, unlockingWallet?.type, unlockingWallet?.rdns]);

  // Commneted below was to render wallets detail screens (eg hardware wallets path selection)
  // return selectedWalletObject?.render ? (
  //   <>
  //     {selectedWalletObject.render({
  //       onProceed: wallet =>
  //         // For Wallet like Private Key or Hardware wallet, we do not care about delegated key ATM.
  //         // Those wallets will most likely will not be included in v4 release.
  //         handleUnlock(wallet, false, {
  //           referralCode,
  //           afterUnlockCallback,
  //           redirectTo: afterUnlockGoTo,
  //         }),
  //       layout,
  //       onBackFromDetail: () => setSelectedWallet(undefined),
  //     })}
  //   </>
  // ) : (
  // Commented below was to display Instand trading confirmation screen:
  // ) : selectedWallet &&
  //   quickUnlockState === 'default' &&
  //   !delegationDisabled ? (
  // <GenerateAuthorizationForWalletModal
  //   onBack={() => {
  //     setSelectedWallet(undefined);
  //     setQuickUnlockState('default');
  //   }}
  //   onClose={() => staticDispatch(showWalletModal(false))}
  //   onProceed={delegatedKeyType => handleAsyncUnlock(delegatedKeyType)}
  // />

  const discoveredWallets = useMemo(
    () => Object.values(discoveredMap),
    [discoveredMap]
  );

  const staticRdnsList = walletsList
    .filter(wallet => !!wallet.rdns)
    .flatMap(wallet => wallet.rdns);

  const fullWalletsList = walletsList.flatMap(walletItem => {
    if (walletItem.wallet === 'softwareDiscovered') {
      return discoveredWallets
        .filter(
          discovered =>
            !staticRdnsList.includes(discovered.info.rdns) &&
            !HIDDEN_DISCOVERED_WALLET_RDNS.includes(discovered.info.rdns)
        )
        .map(dWallet => ({
          wallet: walletItem.wallet,
          title: dWallet.info.name,
          discovered: dWallet as ProvidersMap[number] | undefined,
        }));
    }
    if (walletItem.wallet.startsWith('softwareDiscovered')) {
      if (!walletItem.rdns) {
        throw new Error(
          'ConfigError: RDNS must be specified for softwareDiscovered'
        );
      }
      const matchRdns = walletItem.rdns.find(
        configRdns => discoveredMap[configRdns]
      );
      return walletItem.mobileOnly && !isMobile
        ? []
        : [
            {
              wallet: walletItem.wallet,
              title: walletItem.title,
              discovered: matchRdns ? discoveredMap[matchRdns] : undefined,
            },
          ];
    }
    return walletItem.mobileOnly && !isMobile
      ? []
      : [
          {
            wallet: walletItem.wallet,
            title: walletItem.title,
            discovered: undefined as ProvidersMap[number] | undefined,
          },
        ];
  });

  const isQrScren =
    unlockingWallet?.type === 'qrSynced' &&
    quickUnlockState === 'default' &&
    !quickUnlockError;

  return (
    <UnlockLayout
      selectedDetail={unlockingWallet?.type}
      isQrScren={isQrScren}
      onClose={() => setUnlockingWallet(undefined)}
      layout={layout}
      closeDisabled={
        quickUnlockState === 'signing' || quickUnlockState === 'success'
      }
      titleOverride={walletSignTitle || 'Unlock Wallet'}
    >
      {termsConfirmationPlaceholder ? (
        termsConfirmationPlaceholder
      ) : isQrScren ? (
        <QrSyncMobile
          onError={errorMessage => {
            setQuickUnlockError(errorMessage);
            setUnlockingWallet(undefined);
          }}
          remember={remember}
          onUnlock={() => {
            setQuickUnlockState('success');
            setTimeout(() => {
              setUnlockingWallet(undefined);
              afterUnlockCallback?.();
            }, 2000);
          }}
        />
      ) : quickUnlockState === 'success' ||
        (quickUnlockState === 'signing' && !walletSignPromptPlaceholder) ? (
        // Special state after signature but before we store & encrypt wallet
        // At this "signing" state, we expect success to be called any second,
        // so to make UI fluent we already display success message
        //  - on error will be immediatelly replaced by error message
        // <div style={{ minHeight: 300 }} />
        <UnlockSuccess />
      ) : walletSignPromptPlaceholder ? (
        <>{walletSignPromptPlaceholder}</>
      ) : (
        <>
          {quickUnlockError && (
            <ErrorPanel marginBottom={8} message={quickUnlockError} />
          )}

          {isMobile && <ConnectedWallet />}

          <ListWrap>
            {fullWalletsList.map(menuItem => (
              <Tooltip
                css={{
                  cursor: 'not-allowed',
                }}
                text={
                  isRestrictedRegion
                    ? 'This feature is not available in your region'
                    : undefined
                }
                key={menuItem.wallet + menuItem.title}
              >
                <WalletMenuItem
                  title={menuItem.title}
                  wallet={menuItem.wallet}
                  walletRdns={menuItem.discovered?.info.rdns}
                  preLoading={
                    !!unlockingWallet &&
                    unlockingWallet.type === menuItem.wallet &&
                    unlockingWallet.rdns === menuItem.discovered?.info.rdns
                  }
                  loading={
                    !!unlockingWallet &&
                    unlockingWallet.type === menuItem.wallet &&
                    unlockingWallet.rdns === menuItem.discovered?.info.rdns &&
                    quickUnlockState === 'unlocking'
                  }
                  disabled={
                    isRestrictedRegion ||
                    quickUnlockState !== 'default' ||
                    !!unlockingWallet
                  }
                  connected={
                    (currentWalletType === menuItem.wallet &&
                      currentWalletRdns === menuItem.discovered?.info.rdns) ||
                    (currentWalletType === 'softwareDiscovered' &&
                      menuItem.wallet.startsWith('softwareDiscovered_') &&
                      currentWalletRdns === menuItem.discovered?.info.rdns)
                  }
                  icon={getWalletIcon(
                    menuItem.wallet,
                    menuItem.discovered?.info.icon
                  )}
                  onSelect={selected => {
                    setQuickUnlockError('');
                    setUnlockingWallet(selected);
                  }}
                />
              </Tooltip>
            ))}
          </ListWrap>

          {!delegationDisabled && (
            <RememberMe
              remember={remember}
              onChange={setRemember}
              disabled={quickUnlockState !== 'default' || !!unlockingWallet}
            />
          )}

          {!isMobile ? <ConnectedWallet /> : <div style={{ padding: 10 }} />}
        </>
      )}
    </UnlockLayout>
  );
}

function UnlockCore(params: Params) {
  const discoveredMap = useDiscoverEip6963Wallets({ waitMs: 100 });

  if (!discoveredMap) {
    // Waiting until initialised
    return (
      <UnlockLayout
        selectedDetail={undefined}
        isQrScren={false}
        // eslint-disable-next-line @typescript-eslint/no-empty-function
        onClose={() => {}}
        layout={params.layout}
        closeDisabled={true}
      >
        {''}
      </UnlockLayout>
    );
  }

  return <UnlockMainCore {...params} discoveredMap={discoveredMap} />;
}

export default UnlockCore;
